import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings,
} from '@mui/icons-material';
import { Layout } from '../components/Layout/Layout';
import { SKUList } from '../components/SKU';
import { useSkus } from '../hooks/useSkus';
import apiClient from '../api';
import { AdjustConfig } from '../types/sku';

interface EditModalState {
  open: boolean;
  config: AdjustConfig | null;
  isNew: boolean;
}

export const AdjustConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<AdjustConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editModal, setEditModal] = useState<EditModalState>({
    open: false,
    config: null,
    isNew: false,
  });

  const [formData, setFormData] = useState({
    appName: '',
    appToken: '',
    events: '{}',
    tvToken: '',
    tvEvents: '{}',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // SKU management for selected config
  const selectedAppName = editModal.config?.appName || null;
  const {
    skus,
    loading: skusLoading,
    totalSkus,
    activeSkus,
    inactiveSkus,
    addSku,
    updateSku,
    deleteSku,
    toggleSku,
    error: skuError,
    clearError: clearSkuError,
  } = useSkus(selectedAppName);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/adjust-config');
      // API returns { success: true, result: { configs: [...] } }
      if (data.success && data.result?.configs && Array.isArray(data.result.configs)) {
        setConfigs(data.result.configs);
      } else if (data.success && Array.isArray(data.data)) {
        setConfigs(data.data);
      } else {
        setConfigs([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleOpenEdit = (config: AdjustConfig) => {
    setFormData({
      appName: config.appName,
      appToken: config.appToken,
      events: JSON.stringify(config.events, null, 2),
      tvToken: config.tvToken || '',
      tvEvents: config.tvEvents ? JSON.stringify(config.tvEvents, null, 2) : '{}',
    });
    setFormErrors({});
    setEditModal({ open: true, config, isNew: false });
  };

  const handleOpenNew = () => {
    setFormData({
      appName: '',
      appToken: '',
      events: '{\n  "trial": "",\n  "converted": "",\n  "cancel": ""\n}',
      tvToken: '',
      tvEvents: '{\n  "trial": "",\n  "converted": "",\n  "cancel": ""\n}',
    });
    setFormErrors({});
    setEditModal({ open: true, config: null, isNew: true });
  };

  const handleCloseEdit = () => {
    setEditModal({ open: false, config: null, isNew: false });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.appName.trim()) {
      errors.appName = 'App name is required';
    }

    if (!formData.appToken.trim()) {
      errors.appToken = 'App token is required';
    }

    try {
      JSON.parse(formData.events);
    } catch {
      errors.events = 'Invalid JSON format';
    }

    // Validate tvEvents only if tvToken is provided
    if (formData.tvToken.trim() && formData.tvEvents.trim()) {
      try {
        JSON.parse(formData.tvEvents);
      } catch {
        errors.tvEvents = 'Invalid JSON format';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload: any = {
        appName: formData.appName,
        appToken: formData.appToken,
        events: JSON.parse(formData.events),
      };

      // Only include TV fields if tvToken is provided
      if (formData.tvToken.trim()) {
        payload.tvToken = formData.tvToken;
        payload.tvEvents = formData.tvEvents.trim() ? JSON.parse(formData.tvEvents) : {};
      }

      if (editModal.isNew) {
        await apiClient.post('/adjust-config', payload);
        setSuccess('Configuration created successfully');
      } else {
        await apiClient.put(`/adjust-config/${encodeURIComponent(editModal.config!.appName)}`, payload);
        setSuccess('Configuration updated successfully');
      }

      handleCloseEdit();
      await fetchConfigs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (appName: string) => {
    if (!window.confirm(`Are you sure you want to delete the configuration for "${appName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/adjust-config/${encodeURIComponent(appName)}`);
      setSuccess('Configuration deleted successfully');
      await fetchConfigs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete configuration');
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Settings sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>
                  Adjust Config
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Manage application configurations and SKU mappings
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchConfigs} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenNew}
              >
                Add Config
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Config Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : configs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                No configurations found. Click "Add Config" to create one.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>App Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>App Token</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Events</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKUs</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow
                      key={config._id || config.appName}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenEdit(config)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {config.appName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {config.appToken}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {Object.keys(config.events || {}).slice(0, 3).map((key) => (
                            <Chip
                              key={key}
                              label={key}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {Object.keys(config.events || {}).length > 3 && (
                            <Chip
                              label={`+${Object.keys(config.events).length - 3}`}
                              size="small"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${config.skus?.length || 0} SKUs`}
                          size="small"
                          color={config.skus?.length ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(config)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(config.appName)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Edit/Add Dialog */}
      <Dialog
        open={editModal.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editModal.isNew ? 'Add Adjust Config' : 'Edit Adjust Config'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="App Name"
              value={formData.appName}
              onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
              error={!!formErrors.appName}
              helperText={formErrors.appName}
              required
              fullWidth
              size="small"
              disabled={!editModal.isNew}
              placeholder="com.SuponoHoldings.ColorByNumberArtGames"
            />

            <TextField
              label="App Token"
              value={formData.appToken}
              onChange={(e) => setFormData({ ...formData, appToken: e.target.value })}
              error={!!formErrors.appToken}
              helperText={formErrors.appToken}
              required
              fullWidth
              size="small"
              placeholder="9js4yjoy8nb4"
            />

            <TextField
              label="Events (JSON)"
              value={formData.events}
              onChange={(e) => setFormData({ ...formData, events: e.target.value })}
              error={!!formErrors.events}
              helperText={formErrors.events || 'e.g. { "trial": "abc123", "converted": "xyz789" }'}
              multiline
              rows={4}
              fullWidth
              size="small"
              sx={{ fontFamily: 'monospace' }}
            />

            <Divider sx={{ my: 1 }}>
              <Chip label="Fire TV Configuration (Optional)" size="small" />
            </Divider>

            <TextField
              label="TV App Token (Optional)"
              value={formData.tvToken}
              onChange={(e) => setFormData({ ...formData, tvToken: e.target.value })}
              error={!!formErrors.tvToken}
              helperText={formErrors.tvToken || 'Separate Adjust token for Fire TV devices'}
              fullWidth
              size="small"
              placeholder="9js4yjoy8nb4"
            />

            <TextField
              label="TV Events (JSON, Optional)"
              value={formData.tvEvents}
              onChange={(e) => setFormData({ ...formData, tvEvents: e.target.value })}
              error={!!formErrors.tvEvents}
              helperText={formErrors.tvEvents || 'Event tokens for Fire TV (same format as Events)'}
              multiline
              rows={4}
              fullWidth
              size="small"
              sx={{ fontFamily: 'monospace' }}
              disabled={!formData.tvToken.trim()}
            />

            {/* SKU Management Section - Only show when editing existing config */}
            {!editModal.isNew && editModal.config && (
              <>
                <Divider sx={{ my: 1 }} />

                {skuError && (
                  <Alert severity="error" onClose={clearSkuError} sx={{ mb: 1 }}>
                    {skuError}
                  </Alert>
                )}

                <SKUList
                  skus={skus}
                  loading={skusLoading}
                  totalSkus={totalSkus}
                  activeSkus={activeSkus}
                  inactiveSkus={inactiveSkus}
                  onAdd={addSku}
                  onUpdate={updateSku}
                  onDelete={deleteSku}
                  onToggle={toggleSku}
                />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEdit} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ minWidth: 100 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Layout>
  );
};
